// ============================================================
//  TASK COVERAGE & PAYOUTS PAGE — ADMIN DASHBOARD
//  Path: src/pages/TaskCoveragePage.tsx
//
//  For every hired task: who covers the worker with insurance (their
//  own policy or MatchNBook), whether an individual or a company was
//  hired (and which worker does the job), and how the money is split.
//
//  Everything shown is what was recorded or paid — the backend does all
//  the calculating, this page only displays it. The company/worker split
//  is a DECLARED percentage: Stripe pays the hired party only, and the
//  company pays its worker outside Stripe.
//
//  Access: super_admin, admin only.
// ============================================================

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { getTaskCoverageReport, getTaskCoverageCsv } from '../services/admin.service'
import type { TaskCoverageReport, TaskCoverageRow } from '../types'

const PAGE_SIZE = 20

const TASK_STATUSES = ['assigned', 'pending_completion', 'completed', 'disputed', 'cancelled', 'open', 'full', 'expired']
const PAYMENT_STATUSES = ['pending', 'authorized', 'capturing', 'releasing', 'paid', 'partially_refunded', 'refunding', 'refunded', 'failed']

const COVER_OPTIONS: Array<[string, string]> = [
  ['', 'Anyone'],
  ['own_policy', 'Their own policy'],
  ['matchnbook', 'MatchNBook'],
  ['mixed', 'Mixed (own policy + MatchNBook)'],
  ['none', 'No insurance'],
]
const HIRED_OPTIONS: Array<[string, string]> = [
  ['', 'Anyone'],
  ['individual', 'An individual'],
  ['company', 'A company'],
]

const BASIS_LABELS: Record<string, string> = {
  final:    'final (Stripe net)',
  recorded: 'recorded payout',
  expected: 'expected — not paid yet / no record',
  none:     'nothing paid',
}
const ACTION_LABELS: Record<string, string> = {
  assigned:     'Assigned',
  reassigned:   'Reassigned',
  rate_changed: 'Percentage changed',
  removed:      'Removed',
}
const CHANGED_BY_LABELS: Record<string, string> = {
  company: 'by the company',
  worker:  'by the worker',
  system:  'automatically',
}

const label = (s: string) => s.replace(/_/g, ' ')
const money = (n: number | null | undefined) => (n === null || n === undefined ? '—' : `$${Number(n).toFixed(2)}`)
const pct = (n: number | null | undefined) => (n === null || n === undefined ? '—' : `${Number(n)}%`)
const when = (s: string | null | undefined) => (s ? new Date(s).toLocaleString() : '—')
const day = (s: string | null | undefined) => (s ? new Date(s).toLocaleDateString() : '—')

const errorMessage = (err: any, fallback: string) =>
  err?.response?.data?.message || (err?.isNetworkError
    ? 'Could not reach the server — check your connection and try again.'
    : fallback)

const coverClass = (c: string) =>
  c === 'matchnbook' ? 'bg-blue-600/20 text-blue-400'
    : c === 'own_policy' ? 'bg-green-600/20 text-green-400'
    : c === 'mixed' ? 'bg-purple-600/20 text-purple-400'
    : 'bg-gray-600/20 text-gray-400'

const statusClass = (s: string) =>
  s === 'completed' ? 'bg-green-600/20 text-green-400'
    : s === 'cancelled' ? 'bg-red-600/20 text-red-400'
    : s === 'disputed' ? 'bg-orange-600/20 text-orange-400'
    : 'bg-gray-600/20 text-gray-400'

type Filters = {
  from: string; to: string; taskStatus: string; paymentStatus: string
  coveredBy: string; hiredBy: string; search: string
  companyId: string; companyLabel: string; workerId: string; workerLabel: string
}
const EMPTY: Filters = {
  from: '', to: '', taskStatus: '', paymentStatus: '', coveredBy: '', hiredBy: '', search: '',
  companyId: '', companyLabel: '', workerId: '', workerLabel: '',
}

// Only non-empty filters go to the server.
const toParams = (f: Filters) => {
  const p: Record<string, string> = {}
  if (f.from) p.from = f.from
  if (f.to) p.to = f.to
  if (f.taskStatus) p.taskStatus = f.taskStatus
  if (f.paymentStatus) p.paymentStatus = f.paymentStatus
  if (f.coveredBy) p.coveredBy = f.coveredBy
  if (f.hiredBy) p.hiredBy = f.hiredBy
  if (f.companyId) p.companyId = f.companyId
  if (f.workerId) p.workerId = f.workerId
  if (f.search.trim()) p.search = f.search.trim()
  return p
}

const inputCls = 'bg-surface-tertiary border border-border-light rounded-xl px-3 py-2 text-sm text-white focus:border-brand-500 focus:outline-none'

const Field: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <label className="flex flex-col gap-1 text-xs text-gray-400">
    {title}
    {children}
  </label>
)

const Row: React.FC<{ k: string; children: React.ReactNode }> = ({ k, children }) => (
  <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-border-light/50 last:border-0">
    <span className="text-gray-400 shrink-0">{k}</span>
    <span className="text-white text-right">{children}</span>
  </div>
)

const TaskCoveragePage: React.FC = () => {
  const [draft, setDraft]       = useState<Filters>(EMPTY)      // what the inputs show
  const [filters, setFilters]   = useState<Filters>(EMPTY)      // what was applied
  const [page, setPage]         = useState(1)
  const [data, setData]         = useState<TaskCoverageReport | null>(null)
  const [loading, setLoading]   = useState(true)
  const [loadError, setLoadError] = useState('')
  const [selected, setSelected] = useState<TaskCoverageRow | null>(null)
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState('')
  const [notice, setNotice]     = useState('')
  const requestSeq = useRef(0)

  const load = useCallback(async () => {
    const seq = ++requestSeq.current
    setLoading(true)
    setLoadError('')
    try {
      const res = await getTaskCoverageReport({ ...toParams(filters), page, limit: PAGE_SIZE })
      if (seq !== requestSeq.current) return
      setData(res.data.data as TaskCoverageReport)
    } catch (err: any) {
      if (seq !== requestSeq.current) return
      setData(null)
      setLoadError(errorMessage(err, 'Failed to load the report.'))
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [filters, page])

  useEffect(() => { load() }, [load])

  const apply = (e?: React.FormEvent) => {
    e?.preventDefault()
    setSelected(null)
    setPage(1)
    setFilters(draft)
  }
  const clear = () => { setDraft(EMPTY); setFilters(EMPTY); setPage(1); setSelected(null) }

  // "Only this company" / "only this worker": the report filters by id,
  // so the detail panel offers it instead of making admins copy ids.
  const focus = (kind: 'company' | 'worker', id: string, name: string | null) => {
    const next = kind === 'company'
      ? { ...draft, companyId: id, companyLabel: name || 'this company' }
      : { ...draft, workerId: id, workerLabel: name || 'this worker' }
    setDraft(next); setFilters(next); setPage(1); setSelected(null)
  }

  const downloadCsv = async () => {
    setDownloading(true)
    setDownloadError('')
    setNotice('')
    try {
      const res = await getTaskCoverageCsv(toParams(filters))
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `task-coverage-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      if (String(res.headers?.['x-report-truncated']) === 'true') {
        setNotice('The file stopped at the row limit. Narrow the dates or filters and download again to get the rest.')
      }
    } catch (err: any) {
      // A failed blob request carries its JSON error as a Blob.
      let msg = errorMessage(err, 'Could not download the CSV.')
      try {
        const body = err?.response?.data
        if (body instanceof Blob) msg = JSON.parse(await body.text())?.message || msg
      } catch { /* keep the fallback message */ }
      setDownloadError(msg)
    } finally {
      setDownloading(false)
    }
  }

  const rows = data?.rows || []
  const total = data?.total || 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const hasFilter = JSON.stringify(toParams(filters)) !== '{}'

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Task coverage &amp; payouts</h1>
          <p className="text-gray-400 text-sm mt-1">
            Who covers the worker with insurance, who was hired, and how the money was split.
            {!loadError && data ? ` ${total} task${total === 1 ? '' : 's'}.` : ''}
          </p>
        </div>
        <button onClick={downloadCsv} disabled={downloading || !!loadError}
          className="px-4 py-2 text-sm rounded-xl bg-brand-600 text-white hover:bg-brand-500 disabled:opacity-50 transition-colors shrink-0">
          {downloading ? 'Preparing…' : 'Download CSV'}
        </button>
      </div>

      {/* ── Filters ── */}
      <form onSubmit={apply} className="bg-surface-card border border-border-light rounded-2xl p-4 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field title="Hired from">
            <input type="date" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} className={inputCls} />
          </Field>
          <Field title="Hired to">
            <input type="date" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} className={inputCls} />
          </Field>
          <Field title="Insurance covered by">
            <select value={draft.coveredBy} onChange={(e) => setDraft({ ...draft, coveredBy: e.target.value })} className={inputCls}>
              {COVER_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field title="Hired">
            <select value={draft.hiredBy} onChange={(e) => setDraft({ ...draft, hiredBy: e.target.value })} className={inputCls}>
              {HIRED_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field title="Task status">
            <select value={draft.taskStatus} onChange={(e) => setDraft({ ...draft, taskStatus: e.target.value })} className={inputCls}>
              <option value="">Any</option>
              {TASK_STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
            </select>
          </Field>
          <Field title="Payment status">
            <select value={draft.paymentStatus} onChange={(e) => setDraft({ ...draft, paymentStatus: e.target.value })} className={inputCls}>
              <option value="">Any</option>
              {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
            </select>
          </Field>
          <div className="col-span-2">
            <Field title="Search (task, customer, tasker, company or worker name)">
              <input type="text" maxLength={200} value={draft.search} onChange={(e) => setDraft({ ...draft, search: e.target.value })}
                placeholder="e.g. Acme Plumbing" className={inputCls} />
            </Field>
          </div>
        </div>

        {(draft.companyId || draft.workerId) && (
          <div className="flex flex-wrap gap-2 mt-3">
            {draft.companyId && (
              <span className="px-3 py-1 text-xs rounded-full bg-brand-600/20 text-brand-400">
                Company: {draft.companyLabel}
                <button type="button" className="ml-2 hover:text-white" onClick={() => setDraft({ ...draft, companyId: '', companyLabel: '' })}>✕</button>
              </span>
            )}
            {draft.workerId && (
              <span className="px-3 py-1 text-xs rounded-full bg-brand-600/20 text-brand-400">
                Worker: {draft.workerLabel}
                <button type="button" className="ml-2 hover:text-white" onClick={() => setDraft({ ...draft, workerId: '', workerLabel: '' })}>✕</button>
              </span>
            )}
          </div>
        )}

        <div className="flex gap-2 mt-4">
          <button type="submit" className="px-4 py-2 text-sm rounded-xl bg-brand-600 text-white hover:bg-brand-500 transition-colors">Apply</button>
          <button type="button" onClick={clear} className="px-4 py-2 text-sm rounded-xl bg-surface-tertiary text-gray-400 hover:text-white transition-colors">Clear</button>
        </div>
      </form>

      {loadError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 mb-6 flex items-center justify-between gap-3">
          <p className="text-sm text-red-400">{loadError}</p>
          <button onClick={() => load()} className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light transition-colors shrink-0">Try again</button>
        </div>
      )}
      {downloadError && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-4 mb-6 flex items-center justify-between">
          <p className="text-sm text-red-400">{downloadError}</p>
          <button onClick={() => setDownloadError('')} className="text-red-400 hover:text-white text-sm">✕</button>
        </div>
      )}
      {notice && (
        <div className="bg-orange-500/5 border border-orange-500/20 rounded-2xl p-4 mb-6 flex items-center justify-between">
          <p className="text-sm text-orange-400">{notice}</p>
          <button onClick={() => setNotice('')} className="text-orange-400 hover:text-white text-sm">✕</button>
        </div>
      )}
      {data?.truncated && (
        <div className="bg-orange-500/5 border border-orange-500/20 rounded-2xl p-4 mb-6">
          <p className="text-sm text-orange-400">
            This filter had to look through a very large number of tasks and stopped early, so the list may be incomplete. Narrow the dates or add another filter.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* ── Table ── */}
        <div className={`bg-surface-card border border-border-light rounded-2xl overflow-hidden ${selected ? 'xl:col-span-2' : 'xl:col-span-3'}`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-400 border-b border-border-light">
                  <th className="px-4 py-3">Task</th>
                  <th className="px-4 py-3">Hired</th>
                  <th className="px-4 py-3">Insurance</th>
                  <th className="px-4 py-3 text-right">Customer paid</th>
                  <th className="px-4 py-3 text-right">Hired party got</th>
                  <th className="px-4 py-3 text-right">Worker / company</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>
                )}
                {!loading && !loadError && rows.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                    {hasFilter ? 'No tasks match these filters.' : 'No hired tasks yet.'}
                  </td></tr>
                )}
                {!loading && rows.map((r) => (
                  <tr key={r.taskId} onClick={() => setSelected(r)}
                    className={`border-b border-border-light/50 cursor-pointer hover:bg-surface-tertiary/50 ${selected?.taskId === r.taskId ? 'bg-surface-tertiary/60' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="text-white font-medium">{r.title}</div>
                      <div className="text-xs text-gray-400">{day(r.hiredAt)} · {r.customerName || 'customer'}</div>
                      <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${statusClass(r.taskStatus)}`}>{label(r.taskStatus)}</span>
                      {r.flags.length > 0 && <span className="ml-2 text-xs text-orange-400" title={r.flags.map((f) => f.message).join('\n')}>⚠️ {r.flags.length}</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white">{r.hiredParty?.name || '—'}</div>
                      <div className="text-xs text-gray-400">
                        {r.hiredBy === 'company' ? `Company · worker: ${r.worker?.name || 'none yet'}` : r.hiredBy === 'individual' ? 'Individual' : ''}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${coverClass(r.coverage.coveredBy)}`}>{r.coverage.label}</span>
                    </td>
                    <td className="px-4 py-3 text-right text-white">
                      {money(r.money.customerNet)}
                      {r.money.refunded > 0 && <div className="text-xs text-gray-400">refunded {money(r.money.refunded)}</div>}
                    </td>
                    <td className="px-4 py-3 text-right text-white">
                      {money(r.money.hiredPartyReceived)}
                      {r.money.receivedBasis === 'expected' && <div className="text-xs text-orange-400">expected</div>}
                    </td>
                    <td className="px-4 py-3 text-right text-white">
                      {r.money.split
                        ? (r.money.split.workerShare === null
                          ? <span className="text-xs text-gray-400">{r.money.split.companyShare === null ? 'not recorded' : `company ${money(r.money.split.companyShare)}`}</span>
                          : <>{money(r.money.split.workerShare)} / {money(r.money.split.companyShare)}<div className="text-xs text-gray-400">{pct(r.money.split.workerRate)} to worker</div></>)
                        : <span className="text-xs text-gray-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border-light text-sm text-gray-400">
              <span>Page {page} of {totalPages}</span>
              <div className="flex gap-2">
                <button disabled={page <= 1 || loading} onClick={() => { setPage(page - 1); setSelected(null) }}
                  className="px-3 py-1.5 rounded-lg bg-surface-tertiary hover:text-white disabled:opacity-40">Previous</button>
                <button disabled={page >= totalPages || loading} onClick={() => { setPage(page + 1); setSelected(null) }}
                  className="px-3 py-1.5 rounded-lg bg-surface-tertiary hover:text-white disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </div>

        {/* ── Detail ── */}
        {selected && (
          <div className="bg-surface-card border border-border-light rounded-2xl p-5 h-fit">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h2 className="text-lg font-semibold text-white">{selected.title}</h2>
                <p className="text-xs text-gray-400">{selected.category || 'No category'} · {label(selected.taskStatus)} · payment {label(selected.paymentStatus)}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-white">✕</button>
            </div>

            {selected.flags.length > 0 && (
              <div className="bg-orange-500/5 border border-orange-500/20 rounded-xl p-3 mb-4 space-y-1">
                {selected.flags.map((f) => <p key={f.code} className="text-xs text-orange-400">⚠️ {f.message}</p>)}
              </div>
            )}

            <h3 className="text-xs uppercase tracking-wide text-gray-400 mb-1">Who was hired</h3>
            <div className="mb-4">
              <Row k="Hired by">{selected.hiredBy === 'company' ? 'A company' : selected.hiredBy === 'individual' ? 'An individual' : '—'}</Row>
              <Row k={selected.hiredBy === 'company' ? 'Company' : 'Tasker'}>{selected.hiredParty?.name || '—'}</Row>
              {selected.hiredBy === 'company' && <Row k="Company owner">{selected.hiredParty?.ownerName || '—'}</Row>}
              {selected.hiredBy === 'company' && <Row k="Worker on the job">{selected.worker?.name || 'none (company or owner does it)'}</Row>}
              <Row k="Customer">{selected.customerName || '—'}</Row>
              <Row k="Hired">{when(selected.hiredAt)}</Row>
              {selected.completedAt && <Row k="Completed">{when(selected.completedAt)}</Row>}
              {selected.cancelledAt && <Row k="Cancelled">{when(selected.cancelledAt)}{selected.cancellationReason ? ` (${label(selected.cancellationReason)})` : ''}</Row>}
            </div>
            <div className="flex flex-wrap gap-2 mb-5">
              {selected.hiredBy === 'company' && selected.hiredParty && (
                <button onClick={() => focus('company', selected.hiredParty!.expertId, selected.hiredParty!.name)}
                  className="px-3 py-1.5 text-xs rounded-lg bg-surface-tertiary text-gray-300 hover:text-white">Only this company</button>
              )}
              {selected.worker && (
                <button onClick={() => focus('worker', selected.worker!.expertId, selected.worker!.name)}
                  className="px-3 py-1.5 text-xs rounded-lg bg-surface-tertiary text-gray-300 hover:text-white">Only this worker</button>
              )}
            </div>

            <h3 className="text-xs uppercase tracking-wide text-gray-400 mb-1">Insurance — covered by</h3>
            <div className="mb-5">
              <span className={`inline-block px-2 py-0.5 text-xs rounded-full mb-2 ${coverClass(selected.coverage.coveredBy)}`}>{selected.coverage.label}</span>
              {selected.coverage.items.length === 0 && <p className="text-sm text-gray-400">No insurance line applied to this job.</p>}
              {selected.coverage.items.map((i) => (
                <div key={i.type} className="py-2 border-b border-border-light/50 last:border-0 text-sm">
                  <div className="flex justify-between gap-3">
                    <span className="text-white">{i.label}</span>
                    <span className={`px-2 py-0.5 text-xs rounded-full h-fit ${coverClass(i.source)}`}>
                      {i.source === 'own_policy' ? 'own policy' : i.source === 'matchnbook' ? 'MatchNBook' : 'none'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {[i.provider, i.policyRef ? `#${i.policyRef}` : null, i.expiresAt ? `expires ${day(i.expiresAt)}` : null,
                      i.status && !['accepted', 'none'].includes(i.status) ? label(i.status) : null].filter(Boolean).join(' · ') || '—'}
                  </div>
                  {i.source === 'matchnbook' && (
                    <div className="text-xs text-gray-400">
                      Fee collected {money(i.amountCharged)}{i.insurerCost != null ? ` · insurer cost ${money(i.insurerCost)}` : ''}
                    </div>
                  )}
                  {i.note && <div className="text-xs text-gray-500">{i.note}</div>}
                </div>
              ))}
            </div>

            <h3 className="text-xs uppercase tracking-wide text-gray-400 mb-1">Money</h3>
            <div className="mb-2">
              <Row k="Customer paid">{money(selected.money.customerPaid)}</Row>
              {selected.money.refunded > 0 && <Row k="Refunded">{money(selected.money.refunded)}</Row>}
              {selected.money.onHold > 0 && <Row k="On hold on the card">{money(selected.money.onHold)}</Row>}
              <Row k="Customer net">{money(selected.money.customerNet)}</Row>
              <Row k={selected.hiredBy === 'company' ? 'Company received (Stripe)' : 'Tasker received (Stripe)'}>{money(selected.money.hiredPartyReceived)}</Row>
              <Row k="Amount is">{BASIS_LABELS[selected.money.receivedBasis] || selected.money.receivedBasis}</Row>
            </div>

            {selected.money.split && (
              <div className="bg-surface-tertiary/50 rounded-xl p-3 mb-5">
                <p className="text-xs text-gray-400 mb-1">Split between company and worker — declared, paid by the company outside Stripe</p>
                <Row k="Worker's percentage">{pct(selected.money.split.workerRate)}</Row>
                <Row k="Worker share">{money(selected.money.split.workerShare)}</Row>
                <Row k="Company keeps">{money(selected.money.split.companyShare)}</Row>
                {selected.money.split.note && <p className="text-xs text-gray-500 mt-1">{selected.money.split.note}</p>}
              </div>
            )}

            {selected.hiredBy === 'company' && (
              <>
                <h3 className="text-xs uppercase tracking-wide text-gray-400 mb-1">Worker &amp; percentage history</h3>
                {selected.assignmentHistory.length === 0
                  ? <p className="text-sm text-gray-400">No changes recorded.</p>
                  : (
                    <ol className="space-y-2">
                      {selected.assignmentHistory.map((h, idx) => (
                        <li key={idx} className="text-sm border-l-2 border-border-light pl-3">
                          <div className="text-white">
                            {ACTION_LABELS[h.action] || h.action}
                            {h.workerName ? ` — ${h.workerName}` : ''}
                            {h.action === 'reassigned' && h.previousWorkerName ? ` (was ${h.previousWorkerName})` : ''}
                            {h.action === 'removed' && h.previousWorkerName ? ` — ${h.previousWorkerName}` : ''}
                          </div>
                          <div className="text-xs text-gray-400">
                            {h.action === 'removed'
                              ? `was ${pct(h.previousWorkerRate)}`
                              : h.previousWorkerRate !== null && h.previousWorkerRate !== h.workerRate
                                ? `${pct(h.previousWorkerRate)} → ${pct(h.workerRate)}`
                                : pct(h.workerRate)}
                            {' · '}{when(h.at)}{' · '}{CHANGED_BY_LABELS[h.changedBy] || h.changedBy}
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default TaskCoveragePage